import type { LayoutItem, TagLayout } from "./types";
import { writeBarcode } from "./zxing";

// ^A0 average glyph width and ascent
const A0_WIDTH = 0.52;
const A0_ASCENT = 0.8;

const ANCHOR = { L: "start", C: "middle", R: "end" } as const;

type Field = Record<string, string[]>;

// one rectangle per horizontal run of black, each dot cell × cell
const runs = (
	w: number,
	h: number,
	black: (x: number, y: number) => boolean,
	x0: number,
	y0: number,
	cell: number,
) => {
	let d = "";
	for (let y = 0; y < h; y++)
		for (let x = 0; x < w; ) {
			if (!black(x, y)) {
				x++;
				continue;
			}
			const start = x;
			while (x < w && black(x, y)) x++;
			const len = (x - start) * cell;
			d += `M${x0 + start * cell} ${y0 + y * cell}h${len}v${cell}h-${len}z`;
		}
	return d;
};

// ^FH _XX hex; \\ is a backslash inside ^FB
const fieldData = (f: Field) =>
	(f.FD?.[0] ?? "").replace(/_([0-9A-F]{2})|\\\\/gi, (m, hex) =>
		hex ? String.fromCharCode(Number.parseInt(hex, 16)) : f.FB ? "\\" : m,
	);

const toItem = async (f: Field): Promise<Omit<LayoutItem, "id"> | null> => {
	const [x, y] = (f.FO ?? []).map(Number);
	if (f.A0) {
		const s = fieldData(f);
		const size = Number(f.A0[1]);
		const len = Number(f.FB?.[0] ?? 0);
		const align = (f.FB?.[3] ?? "L") as keyof typeof ANCHOR;
		const tw = Math.round(s.length * size * A0_WIDTH);
		const lx = align === "C" ? Math.trunc(len / 2) : align === "R" ? len : 0;
		return {
			s,
			size,
			anchor: ANCHOR[align],
			ax: x + lx,
			by: y + Math.round(size * A0_ASCENT),
			tw,
			over: tw > len || undefined,
		};
	}
	if (f.BQ) {
		const data = fieldData(f);
		// QA,<text>: EC level and input mode first
		const s = data.slice(data.indexOf(",") + 1);
		const mag = Number(f.BQ[2]);
		// mask 7 as ^BQN,2,mag,Q,7 asks
		const { error, symbol } = await writeBarcode(s, {
			format: "QRCode",
			options: "ecLevel=Q,dataMask=7",
		});
		if (error) throw new Error(error);
		const n = symbol.width;
		return {
			d: runs(n, n, (cx, cy) => symbol.data[cy * n + cx] === 0, x, y, mag),
		};
	}
	if (f.GB) {
		const [w, h, t] = f.GB.map(Number);
		let d = `M${x} ${y}h${w}v${h}h-${w}z`;
		if (2 * t < w && 2 * t < h)
			d += `M${x + t} ${y + t}v${h - 2 * t}h${w - 2 * t}v-${h - 2 * t}z`;
		return { d };
	}
	if (f.GF) {
		// A,bytes,bytes,stride,hex; rows MSB first, 1 = black
		const stride = Number(f.GF[3]);
		const bits =
			(f.GF[4] ?? "").match(/../g)?.map((b) => Number.parseInt(b, 16)) ?? [];
		const w = stride * 8;
		const h = bits.length / stride;
		const black = (cx: number, cy: number) =>
			(bits[cy * stride + (cx >> 3)] & (0x80 >> (cx & 7))) !== 0;
		return { d: runs(w, h, black, x, y, 1) };
	}
	return null;
};

// Only the ZPL PRT.FormatTagZpl writes.
export const zplToLayout = async (zpl: string): Promise<TagLayout> => {
	const layout: TagLayout = { w: 0, h: 0, items: [] };
	let f: Field = {};
	for (const part of zpl.split("^")) {
		const code = part.slice(0, 2).toUpperCase();
		const args = part.slice(2);
		if (code === "PW") layout.w = Number(args);
		else if (code === "LL") layout.h = Number(args);
		else if (code === "FS") {
			const item = await toItem(f);
			if (item) layout.items.push({ id: layout.items.length + 1, ...item });
			f = {};
		} else f[code] = code === "FD" ? [args] : args.split(",");
	}
	return layout;
};
